import axios from 'axios';
import { store } from 'redux/store';
import {
  setServerAlias, setServerVersion, addMessage, setSessionTimeoutInSeconds, setSessionWarningTimeoutInSeconds, mountView
} from 'redux/actions/global';
import { Panel, PanelPresets } from 'utility/enumerations';
import { globalEndpoints } from 'endpoints/global';
import { SET_CAPTCHA_NORMAL } from 'redux/types/security';
import { END_WAIT, WAPI_REFETCH } from 'redux/types/global';
import { RESET_DEMOGRAPHICS_COMPARE } from 'redux/types/demographics';
import { RESET_MMTCS, RESET_MMTC_PROFILE } from 'redux/types/dispenser';
import { RESET_PATIENT_PROFILE, RESET_PATIENT_ORDERS } from 'redux/types/patient';
import { RESET_CERTIFICATION_ID, RESET_UNCLOSED_CERTIFICATION_ID } from 'redux/types/physician';
import { RESET_ORGS, RESET_ORG_PROFILE } from 'redux/types/organization';
import { RESET_CARDHOLDERINFO } from 'redux/types/suspension';
import { RESET_DECEASED_USERS, RESET_DUPLICATE_USERS } from 'redux/types/tools';
import { RESET_TRANSPORTER_PROFILE, RESET_TRANSPORTERS } from 'redux/types/transporter';
import {
  RESET_USERS, RESET_USER_PROFILE, RESET_USER_ROLES, RESET_USER_STATUS, RESET_NEWUSER_APPLICANTS
} from 'redux/types/user';

const MAX_REQUESTS_COUNT = 1;
const INTERVAL_MS = 50;
let PENDING_REQUESTS = 0;

axios.defaults.withXSRFToken = true;

const mmurApi = axios.create({
  baseURL: `${globalEndpoints.apiUrl}`,
  withCredentials: true,
  xsrfHeaderName: 'X-Csrf-Token',
  timeout: 6000000
});

export function processMessages(dispatch, messages) {
  if (dispatch && messages && messages.length > 0) {
    messages.forEach((item) => {
      const message = {
        type: item.level ? item.level.toLowerCase() : 'error',
        text: item.messageContent.startsWith(': ') && item.messageContent.length > 2 ? item.messageContent.substring(2, item.messageContent.length) : item.messageContent,
      };
      dispatch(addMessage(message));
    });
  }
}

export function errorHandler(dispatch, error) {
  if (!dispatch) {
    return;
  }
  dispatch({
    type: END_WAIT,
  });
  if (error?.response?.status === 400) {
    if (error?.response?.data?.messages?.some((r) => r.messageContent === 'Captcha failed.')) {
      dispatch({
        type: SET_CAPTCHA_NORMAL,
      });
      return;
    }
  }
  if (error?.response?.status === 401) {
    const requestUrl = error?.config?.url?.toLowerCase() || '';
    if (requestUrl.includes('/security/logout')) {
      return;
    }
    dispatch(
      mountView({
        viewPath: 'security/logout', // the logout component handles the reset of the store properly
        targetPanel: Panel.Primary,
        panelLayout: PanelPresets.OneFull,
        clear: true
      })
    );
  }
  if (error?.response?.status === 403) {
    dispatch({ type: RESET_DEMOGRAPHICS_COMPARE });
    dispatch({ type: RESET_MMTCS });
    dispatch({ type: RESET_MMTC_PROFILE });
    dispatch({ type: RESET_PATIENT_PROFILE });
    dispatch({ type: RESET_PATIENT_ORDERS });
    dispatch({ type: RESET_CERTIFICATION_ID });
    dispatch({ type: RESET_UNCLOSED_CERTIFICATION_ID });
    dispatch({ type: RESET_ORGS });
    dispatch({ type: RESET_ORG_PROFILE });
    dispatch({ type: RESET_CARDHOLDERINFO });
    dispatch({ type: RESET_DECEASED_USERS });
    dispatch({ type: RESET_DUPLICATE_USERS });
    dispatch({ type: RESET_TRANSPORTER_PROFILE });
    dispatch({ type: RESET_TRANSPORTERS });
    dispatch({ type: RESET_USERS });
    dispatch({ type: RESET_USER_PROFILE });
    dispatch({ type: RESET_USER_ROLES });
    dispatch({ type: RESET_USER_STATUS });
    dispatch({ type: RESET_NEWUSER_APPLICANTS });
  }

  if (error?.response?.data) {
    processMessages(dispatch, error.response.data.messages);
  }
}

mmurApi.interceptors.response.use((response) => {
  const sessionTimeoutInSeconds = response?.headers?.['x-session-timeout'];
  const sessionWarningTimeoutInSeconds = response?.headers?.['x-session-timeout-warning'];

  if (sessionTimeoutInSeconds && sessionWarningTimeoutInSeconds) {
    store.dispatch(setSessionTimeoutInSeconds(sessionTimeoutInSeconds));
    store.dispatch(setSessionWarningTimeoutInSeconds(sessionWarningTimeoutInSeconds));
    store.dispatch({ type: WAPI_REFETCH });
  }

  return response;
});

mmurApi.interceptors.response.use((response) => {
  PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1);
  processMessages(store.dispatch, response.data.messages);
  return response;
}, (error) => {
  errorHandler(store.dispatch, error);
  PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1);
  return Promise.reject(error);
});

mmurApi.interceptors.response.use(
  (response) => {
    const serverAlias = response?.headers?.['X-Server-Alias'];
    const serverVersion = response?.headers?.['X-Server-Version'];

    if (serverAlias) {
      store.dispatch(setServerAlias(serverAlias));
    }

    if (serverVersion) {
      store.dispatch(setServerVersion(serverVersion));
    }

    return response;
  },
  (error) => Promise.reject(error),
);

mmurApi.interceptors.request.use(
  (config) => {
    return new Promise((resolve) => {
    // eslint-disable-next-line prefer-const
      let interval = setInterval(() => {
        if (PENDING_REQUESTS < MAX_REQUESTS_COUNT) {
          PENDING_REQUESTS += 1;
          clearInterval(interval);
          resolve(config);
        }
      }, INTERVAL_MS);
    });
  }
);

mmurApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle the error without retrying
    return Promise.reject(error);
  }
);

export { mmurApi };
